import React, { useEffect, useRef } from 'react';
import { CloseButton1 } from '../../Templates';
import './FlowSheet.css';

/**
 * Bottom-sheet shell for the detailed flow step. Backdrop + scroll-lock +
 * Escape/backdrop-click close + focus-in. Single top-right close (shared
 * CloseButton1). On desktop it centers instead of docking to the bottom.
 */
export default function FlowSheet({ open, onClose, title, children }) {
  const sheetRef = useRef(null);
  // Keep onClose in a ref so the open-effect doesn't re-run (and steal focus from
  // inputs) every time the parent re-renders with a new onClose function identity.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && onCloseRef.current) onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    document.documentElement.classList.add('flow-sheet-lock');
    document.body.classList.add('flow-sheet-lock');
    if (sheetRef.current) sheetRef.current.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.documentElement.classList.remove('flow-sheet-lock');
      document.body.classList.remove('flow-sheet-lock');
    };
    // Only re-run when the sheet opens/closes — NOT on every onClose identity change.
  }, [open]);

  if (!open) return null;

  return (
    <div className="flow-sheet-backdrop" data-testid="flow-sheet-backdrop" onClick={onClose}>
      <div
        className="flow-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title || 'Flow step'}
        tabIndex={-1}
        ref={sheetRef}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flow-sheet-header">
          <h3 className="flow-sheet-title">{title}</h3>
          <CloseButton1 onClick={onClose} />
        </div>
        <div className="flow-sheet-body">{children}</div>
      </div>
    </div>
  );
}
